/**
 * =============================================================================
 * IDS CRM Web - Root Application Component
 * =============================================================================
 *
 * PURPOSE:
 * Root component that sets up routing, authentication context, and global
 * providers for the IDS CRM application.
 *
 * =============================================================================
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ListOriginTracker from './components/ListOriginTracker';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import Approvals from './pages/Approvals';
import Help from './pages/Help';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import CustomerForm from './pages/CustomerForm';
import FarmSiteForm from './pages/FarmSiteForm';
import Pipeline from './pages/Pipeline';
import OpportunityDetail from './pages/OpportunityDetail';
import OpportunityForm from './pages/OpportunityForm';
import OpportunityCreate from './pages/OpportunityCreate';
import OpportunityGroupDetail from './pages/OpportunityGroupDetail';
import Inbox from './pages/Inbox';
import QuoteBuilder from './pages/QuoteBuilder';
import QuoteDetail from './pages/QuoteDetail';
import Assets from './pages/Assets';
import ComponentPool from './pages/ComponentPool';
import AssetDetail from './pages/AssetDetail';
import Hierarchy from './pages/Hierarchy';
import AssetForm from './pages/AssetForm';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import TicketForm from './pages/TicketForm';
import SupportDashboard from './pages/SupportDashboard';
import Tasks from './pages/Tasks';
import TaskDetail from './pages/TaskDetail';
import TaskForm from './pages/TaskForm';
import Engineers from './pages/Engineers';
import EngineerDetail from './pages/EngineerDetail';
import EngineerMap from './pages/EngineerMap';
import AdminSettings from './pages/AdminSettings';
import Distributors from './pages/Distributors';
import MaintenanceTaskDetail from './pages/MaintenanceTaskDetail';
import OnCallRota from './pages/OnCallRota';
import OnCallAdmin from './pages/OnCallAdmin';
import OnCallPayroll from './pages/OnCallPayroll';
import TimeReconciliation from './pages/TimeReconciliation';
import Today from './pages/Today';
import Accommodation from './pages/Accommodation';
import OpsBoard from './pages/OpsBoard';
import Bulletins from './pages/Bulletins';
import BulletinDetail from './pages/BulletinDetail';
import BulletinForm from './pages/BulletinForm';
import './App.css';

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <img src="/ids-logo.jpg" alt="IDS" className="loading-logo-img" />
      <div className="loading-spinner" />
      <span>Loading IDS CRM...</span>
    </div>
  );
}

/**
 * R2 (DL-10): role-aware home — the engineering role lands on Today.
 */
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === 'engineering') {
    return <Navigate to="/today" replace />;
  }
  return <Dashboard />;
}

/**
 * Protected route wrapper - redirects to login if not authenticated.
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

/**
 * Public route wrapper - redirects to dashboard if already authenticated.
 */
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}


/**
 * Main App component with routing.
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Microsoft SSO landing — must stay public; it establishes the session */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes */}
      {/* R2 (DL-10): engineers land on Today — the Dashboard is built from
          pipeline-value cards the engineering role can no longer load. */}
      <Route path="/" element={
        <ProtectedRoute>
          <HomeRoute />
        </ProtectedRoute>
      } />

      <Route path="/customers" element={
        <ProtectedRoute>
          <Customers />
        </ProtectedRoute>
      } />

      <Route path="/customers/new" element={
        <ProtectedRoute>
          <CustomerForm />
        </ProtectedRoute>
      } />

      <Route path="/customers/:id" element={
        <ProtectedRoute>
          <CustomerDetail />
        </ProtectedRoute>
      } />

      <Route path="/customers/:id/edit" element={
        <ProtectedRoute>
          <CustomerForm />
        </ProtectedRoute>
      } />

      <Route path="/hierarchy/:type/:id" element={
        <ProtectedRoute>
          <Hierarchy />
        </ProtectedRoute>
      } />

      <Route path="/customers/:id/sites/new" element={
        <ProtectedRoute>
          <FarmSiteForm />
        </ProtectedRoute>
      } />

      <Route path="/customers/:id/sites/:siteId/edit" element={
        <ProtectedRoute>
          <FarmSiteForm />
        </ProtectedRoute>
      } />

      <Route path="/distributors" element={
        <ProtectedRoute>
          <Distributors />
        </ProtectedRoute>
      } />

      <Route path="/pipeline" element={
        <ProtectedRoute>
          <Pipeline />
        </ProtectedRoute>
      } />

      <Route path="/pipeline/new" element={
        <ProtectedRoute>
          <OpportunityCreate />
        </ProtectedRoute>
      } />

      <Route path="/quotes/new" element={
        <ProtectedRoute>
          <QuoteBuilder />
        </ProtectedRoute>
      } />

      <Route path="/quotes/:id/edit" element={
        <ProtectedRoute>
          <QuoteBuilder />
        </ProtectedRoute>
      } />

      <Route path="/quotes/:id" element={
        <ProtectedRoute>
          <QuoteDetail />
        </ProtectedRoute>
      } />

      <Route path="/inbox" element={
        <ProtectedRoute>
          <Inbox />
        </ProtectedRoute>
      } />

      <Route path="/pipeline/groups/:id" element={
        <ProtectedRoute>
          <OpportunityGroupDetail />
        </ProtectedRoute>
      } />

      <Route path="/pipeline/:id" element={
        <ProtectedRoute>
          <OpportunityDetail />
        </ProtectedRoute>
      } />

      <Route path="/pipeline/:id/edit" element={
        <ProtectedRoute>
          <OpportunityForm />
        </ProtectedRoute>
      } />

      <Route path="/assets" element={
        <ProtectedRoute>
          <Assets />
        </ProtectedRoute>
      } />

      <Route path="/assets/components-pool" element={
        <ProtectedRoute>
          <ComponentPool />
        </ProtectedRoute>
      } />

      <Route path="/assets/new" element={
        <ProtectedRoute>
          <AssetForm />
        </ProtectedRoute>
      } />

      <Route path="/assets/:id" element={
        <ProtectedRoute>
          <AssetDetail />
        </ProtectedRoute>
      } />

      <Route path="/assets/:id/edit" element={
        <ProtectedRoute>
          <AssetForm />
        </ProtectedRoute>
      } />

      <Route path="/tickets" element={
        <ProtectedRoute>
          <Tickets />
        </ProtectedRoute>
      } />

      <Route path="/tickets/new" element={
        <ProtectedRoute>
          <TicketForm />
        </ProtectedRoute>
      } />

      <Route path="/tickets/:id" element={
        <ProtectedRoute>
          <TicketDetail />
        </ProtectedRoute>
      } />

      <Route path="/tickets/:id/edit" element={
        <ProtectedRoute>
          <TicketForm />
        </ProtectedRoute>
      } />

      <Route path="/support" element={
        <ProtectedRoute>
          <SupportDashboard />
        </ProtectedRoute>
      } />

      <Route path="/tasks" element={
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      } />

      <Route path="/tasks/new" element={
        <ProtectedRoute>
          <TaskForm />
        </ProtectedRoute>
      } />

      <Route path="/tasks/:id" element={
        <ProtectedRoute>
          <TaskDetail />
        </ProtectedRoute>
      } />

      <Route path="/tasks/:id/edit" element={
        <ProtectedRoute>
          <TaskForm />
        </ProtectedRoute>
      } />

      <Route path="/maintenance/:id" element={
        <ProtectedRoute>
          <MaintenanceTaskDetail />
        </ProtectedRoute>
      } />

      <Route path="/engineers" element={
        <ProtectedRoute>
          <Engineers />
        </ProtectedRoute>
      } />

      <Route path="/engineers/:id" element={
        <ProtectedRoute>
          <EngineerDetail />
        </ProtectedRoute>
      } />

      <Route path="/map" element={
        <ProtectedRoute>
          <EngineerMap />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      } />

      <Route path="/approvals" element={
        <ProtectedRoute>
          <Approvals />
        </ProtectedRoute>
      } />

      <Route path="/help" element={
        <ProtectedRoute>
          <Help />
        </ProtectedRoute>
      } />

      {/* Ops Planning Board (2026-10-16): every signed-in role; the API decides
          who moves cards and who looks after the columns. */}
      <Route path="/ops-board" element={
        <ProtectedRoute>
          <OpsBoard />
        </ProtectedRoute>
      } />

      <Route path="/accommodation" element={
        <ProtectedRoute>
          <Accommodation />
        </ProtectedRoute>
      } />

      {/* Engineer bulletins (2026-09-13): open to every signed-in role; the
          API decides who sees drafts and who publishes. Static paths first. */}
      <Route path="/bulletins" element={
        <ProtectedRoute>
          <Bulletins />
        </ProtectedRoute>
      } />

      <Route path="/bulletins/new" element={
        <ProtectedRoute>
          <BulletinForm />
        </ProtectedRoute>
      } />

      <Route path="/bulletins/:id/edit" element={
        <ProtectedRoute>
          <BulletinForm />
        </ProtectedRoute>
      } />

      <Route path="/bulletins/:id" element={
        <ProtectedRoute>
          <BulletinDetail />
        </ProtectedRoute>
      } />

      <Route path="/on-call" element={
        <ProtectedRoute>
          <OnCallRota />
        </ProtectedRoute>
      } />

      <Route path="/on-call/admin" element={
        <ProtectedRoute>
          <OnCallAdmin />
        </ProtectedRoute>
      } />

      <Route path="/on-call/payroll" element={
        <ProtectedRoute>
          <OnCallPayroll />
        </ProtectedRoute>
      } />

      <Route path="/engineers/time-reconciliation" element={
        <ProtectedRoute>
          <TimeReconciliation />
        </ProtectedRoute>
      } />

      <Route path="/today" element={
        <ProtectedRoute>
          <Today />
        </ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminSettings />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Root application component.
 */
function App() {
  return (
    <Router>
      <AuthProvider>
        <ListOriginTracker />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
